import React from 'react';
import { Building2, MapPin, Home, Plus, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';

const DeveloperDashboard = ({ user, developments, navigate }) => {
  if (!user || (user.role !== 'developer' && user.role !== 'admin')) return null;

  const statusColor = (status) => {
    switch (status) {
      case 'En Planeación': return 'bg-amber-100 text-amber-800';
      case 'En Construcción': return 'bg-blue-100 text-blue-800';
      case 'Entregado': return 'bg-emerald-100 text-emerald-800';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  return (
    <div className="bg-background border border-border rounded-lg p-4 sm:p-6 mb-6" data-testid="developer-panel">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 sm:mb-6">
        <h2 className="text-lg sm:text-xl md:text-2xl font-bold flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          <Building2 className="w-5 h-5 text-primary" />
          Resumen operativo de desarrolladora
        </h2>
        <Button size="sm" onClick={() => navigate('/add-development')} className="w-full sm:w-auto" data-testid="add-development-btn">
          <Plus className="w-4 h-4 mr-1" /> Nuevo Desarrollo
        </Button>
      </div>

      {developments.length === 0 ? (
        <div className="text-center py-10 bg-muted/30 rounded-lg border border-dashed border-border">
          <Building2 className="w-12 h-12 text-muted-foreground/40 mx-auto mb-3" />
          <p className="text-muted-foreground mb-4">Aún no tienes desarrollos registrados. Cuando elimines alguno, podrás restaurarlo desde la sección de eliminados sin perder su historial.</p>
          <Button onClick={() => navigate('/add-development')}>Crear mi primer desarrollo</Button>
        </div>
      ) : (
        <div className="space-y-3">
          {developments.map((dev) => (
            <div
              key={dev.id}
              className="flex flex-col sm:flex-row border border-border/60 rounded-lg overflow-hidden hover:border-primary/40 hover:shadow-md transition-all"
              data-testid={`dev-card-${dev.id}`}
            >
              <div className="sm:w-48 md:w-56 h-36 sm:h-auto bg-muted relative overflow-hidden shrink-0">
                <img
                  src={dev.cover_image_url || dev.image_urls?.[0] || 'https://images.pexels.com/photos/323780/pexels-photo-323780.jpeg'}
                  alt={dev.name}
                  className="w-full h-full object-cover"
                />
                <span className={`absolute top-2 left-2 text-xs font-semibold px-2 py-0.5 rounded-full ${statusColor(dev.status)}`}>
                  {dev.status || 'En Construcción'}
                </span>
              </div>
              <div className="flex-1 p-3 sm:p-4 flex flex-col justify-between min-w-0">
                <div>
                  <h3 className="font-bold text-base mb-1 truncate">{dev.name}</h3>
                  {dev.address && (
                    <p className="text-xs text-muted-foreground flex items-center mb-1 truncate">
                      <MapPin className="w-3 h-3 mr-1 shrink-0" /> {dev.address}
                    </p>
                  )}
                  {dev.description && (
                    <p className="text-xs text-muted-foreground line-clamp-1 mb-2">{dev.description}</p>
                  )}
                </div>
                <div className="flex items-center justify-between pt-2 border-t border-border/40">
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Home className="w-3.5 h-3.5" /> {dev.property_count || 0} propiedades
                  </span>
                  <Button size="sm" variant="outline" onClick={() => navigate(`/development/${dev.id}`)}>
                    <Eye className="w-3 h-3 mr-1" /> Ver
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export { DeveloperDashboard };
